/**
 * Extractor YAML as a structured model — W5.1 (docs/extractors-requirements.md §W5).
 *
 * The guided create page (W5.2+) edits an `ExtractorModel` and renders YAML
 * from it; the workbench keeps YAML as the truth and only reads the model.
 * So the two functions here have to be faithful in BOTH directions over the
 * whole corpus grammar (docs/inventory/extractor-grammar.md, W5.0):
 *
 *   top level   schema-version, extractor-version, extractor-id, integration,
 *               vendor-name, product-name, source-name, source-type,
 *               source-description, kv-regex, provides-streams, master-filters,
 *               globals, event-details
 *   per rule    first-match, decoder (regex | json | custom), decoder-regex,
 *               event-key-format, decoder-header (CEF | LEEF),
 *               event-key-mapping { <key>: { annotate, translate } },
 *               fallback, globals, subs, translate
 *
 * Rules the corpus forced:
 *   - Mapping keys keep ORDER and TYPE. A JS object reorders integer-like keys
 *     ('100', '200' …) and stringifies numeric ones (4624), both of which the
 *     backend would see as a different mapping; everything user-keyed is an
 *     ordered `Entries` list and a key may be a number.
 *   - Scalars keep their YAML type, and the type rules are PyYAML's (YAML 1.1,
 *     `extractor-yaml-schema.ts`), because that is what the backend reads:
 *     `extractor-id: 730` is the number 730 and comes back plain; `'200'` is a
 *     string and comes back quoted; `0059` and `0102043039` are STRINGS;
 *     plain `yes` is a boolean, so the string is written `'yes'`; `.` is a
 *     string. `schema-version` is the one float and is kept as its source
 *     text so `1.0` does not become `1`.
 *   - Unknown keys anywhere are kept (`extra`) and written back after the
 *     known ones. The form treats a model with `extra` as hand-edited.
 *   - Output style is the backend's own dumper style: sequences with the dash
 *     at the parent's column, single quotes, no line folding, an empty value
 *     written bare, and the corpus's `? ''` idiom for the empty event key.
 *
 * `fromYaml` never guesses: a document that is not a mapping, or that fails
 * to parse, throws `ExtractorYamlError` with the parser's message.
 */

import YAML, { Scalar, YAMLMap, YAMLSeq, isMap, isScalar, isSeq } from 'yaml'
import { pyyamlTags } from './extractor-yaml-schema'

export type YamlScalar = string | number | boolean | null
export type YamlKey = string | number
/** An ordered list of key/value pairs — a YAML mapping with its order and key types intact. */
export type Entries<V = YamlScalar> = Array<[YamlKey, V]>
/** A raw YAML value outside the typed grammar: scalars, arrays, and mappings as `Map` (order and key types kept). */
export type RawValue = YamlScalar | RawValue[] | Map<YamlKey, RawValue>
/** A sequence item: a scalar, or (a corpus typo, `- SYSTEM:`) a mapping. */
export type SeqItem = YamlScalar | Map<YamlKey, RawValue>

export interface MappingBlock {
  annotate?: Entries
  translate?: Entries
  extra: Entries<RawValue>
}

export interface KeyMappingModel extends MappingBlock {
  key: YamlKey
}

export interface EventRuleModel {
  firstMatch?: YamlScalar
  decoder?: YamlScalar
  decoderRegex?: YamlScalar
  keyFormat?: YamlScalar
  decoderHeader?: YamlScalar
  mappings: KeyMappingModel[]
  fallback?: MappingBlock
  globals?: MappingBlock
  /** Per-field value substitutions: `[field, [[from, to], …]]`. */
  subs?: Entries<Entries>
  translate?: Entries
  extra: Entries<RawValue>
}

export interface ExtractorModel {
  /** Kept as source text (`1.0`), the one float in the grammar. */
  schemaVersion?: string
  extractorVersion?: YamlScalar
  extractorId?: YamlScalar
  integration?: YamlScalar
  vendorName?: YamlScalar
  productName?: YamlScalar
  sourceName?: YamlScalar
  sourceType?: YamlScalar
  sourceDescription?: YamlScalar
  kvRegex?: YamlScalar
  providesStreams: SeqItem[]
  masterFilters: SeqItem[]
  /** Top-level globals — one corpus extractor has it. */
  globals?: MappingBlock
  rules: EventRuleModel[]
  extra: Entries<RawValue>
}

const YAML_OPTS = { version: '1.1', customTags: pyyamlTags, uniqueKeys: false } as const

export class ExtractorYamlError extends Error {
  override name = 'ExtractorYamlError'
}

// ---------------------------------------------------------------- reading

const TOP_KEYS = ['schema-version', 'extractor-version', 'extractor-id', 'integration', 'vendor-name', 'product-name', 'source-name', 'source-type', 'source-description', 'kv-regex', 'provides-streams', 'master-filters', 'globals', 'event-details'] as const
const RULE_KEYS = ['first-match', 'decoder', 'decoder-regex', 'event-key-format', 'decoder-header', 'event-key-mapping', 'fallback', 'globals', 'subs', 'translate'] as const
const BLOCK_KEYS = ['annotate', 'translate'] as const

function keyOf(node: unknown): YamlKey {
  if (isScalar(node)) {
    const v = node.value
    if (typeof v === 'number') return v
    return v === null || v === undefined ? '' : String(v)
  }
  return String(node)
}

function scalarOf(node: unknown): YamlScalar {
  if (node === null || node === undefined) return null
  if (isScalar(node)) {
    const v = node.value
    if (v === null || v === undefined) return null
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v
    return String(v)
  }
  // A collection where a scalar was expected: keep it readable, never throw.
  return JSON.stringify(rawOf(node))
}

function rawOf(node: unknown): RawValue {
  if (isScalar(node)) return scalarOf(node)
  if (isSeq(node)) return node.items.map(rawOf)
  if (isMap(node)) return new Map(entriesOf(node, rawOf))
  return null
}

function entriesOf<V>(map: YAMLMap, read: (node: unknown) => V): Entries<V> {
  return map.items.map((pair) => [keyOf(pair.key), read(pair.value)] as [YamlKey, V])
}

function scalarEntries(node: unknown): Entries {
  return isMap(node) ? entriesOf(node, scalarOf) : []
}

function seqOf(node: unknown): SeqItem[] {
  if (isSeq(node)) return node.items.map((x) => (isMap(x) ? new Map(entriesOf(x, rawOf)) : scalarOf(x)))
  if (node === null || node === undefined) return []
  return [scalarOf(node)]
}

function blockOf(node: unknown): MappingBlock {
  const block: MappingBlock = { extra: [] }
  if (!isMap(node)) return block
  for (const pair of node.items) {
    const k = keyOf(pair.key)
    if (k === 'annotate') block.annotate = scalarEntries(pair.value)
    else if (k === 'translate') block.translate = scalarEntries(pair.value)
    else block.extra.push([k, rawOf(pair.value)])
  }
  return block
}

function ruleOf(node: unknown): EventRuleModel {
  const rule: EventRuleModel = { mappings: [], extra: [] }
  if (!isMap(node)) return rule
  for (const pair of node.items) {
    const k = keyOf(pair.key)
    const v = pair.value
    switch (k) {
      case 'first-match': rule.firstMatch = scalarOf(v); break
      case 'decoder': rule.decoder = scalarOf(v); break
      case 'decoder-regex': rule.decoderRegex = scalarOf(v); break
      case 'event-key-format': rule.keyFormat = scalarOf(v); break
      case 'decoder-header': rule.decoderHeader = scalarOf(v); break
      case 'event-key-mapping':
        rule.mappings = isMap(v) ? v.items.map((m) => ({ key: keyOf(m.key), ...blockOf(m.value) })) : []
        break
      case 'fallback': rule.fallback = blockOf(v); break
      case 'globals': rule.globals = blockOf(v); break
      case 'subs': rule.subs = isMap(v) ? entriesOf(v, scalarEntries) : []; break
      case 'translate': rule.translate = scalarEntries(v); break
      default: rule.extra.push([k, rawOf(v)])
    }
  }
  return rule
}

/** Parse extractor YAML into the model. Throws `ExtractorYamlError` on a document that is not a mapping. */
export function fromYaml(yaml: string): ExtractorModel {
  const doc = YAML.parseDocument(yaml, YAML_OPTS)
  if (doc.errors.length) throw new ExtractorYamlError(doc.errors.map((e) => e.message.split('\n')[0]).join('; '))
  const root = doc.contents
  if (!isMap(root)) throw new ExtractorYamlError(root === null || root === undefined ? 'The document is empty' : 'The document is not a mapping')
  const model: ExtractorModel = { providesStreams: [], masterFilters: [], rules: [], extra: [] }
  for (const pair of root.items) {
    const k = keyOf(pair.key)
    const v = pair.value
    switch (k) {
      case 'schema-version': model.schemaVersion = isScalar(v) && v.range ? yaml.slice(v.range[0], v.range[1]) : String(scalarOf(v) ?? ''); break
      case 'extractor-version': model.extractorVersion = scalarOf(v); break
      case 'extractor-id': model.extractorId = scalarOf(v); break
      case 'integration': model.integration = scalarOf(v); break
      case 'vendor-name': model.vendorName = scalarOf(v); break
      case 'product-name': model.productName = scalarOf(v); break
      case 'source-name': model.sourceName = scalarOf(v); break
      case 'source-type': model.sourceType = scalarOf(v); break
      case 'source-description': model.sourceDescription = scalarOf(v); break
      case 'kv-regex': model.kvRegex = scalarOf(v); break
      case 'provides-streams': model.providesStreams = seqOf(v); break
      case 'master-filters': model.masterFilters = seqOf(v); break
      case 'globals': model.globals = blockOf(v); break
      case 'event-details': model.rules = isSeq(v) ? v.items.map(ruleOf) : []; break
      default: model.extra.push([k, rawOf(v)])
    }
  }
  return model
}

// ---------------------------------------------------------------- writing

const OUT = { lineWidth: 0, singleQuote: true, indentSeq: false, nullStr: '' } as const

type Doc = YAML.Document
type Node = Scalar | YAMLMap | YAMLSeq

function mapNode(doc: Doc, entries: Array<[YamlKey, unknown]>): YAMLMap {
  const m = new YAMLMap(doc.schema)
  for (const [k, v] of entries) m.add(doc.createPair(k, rawNode(doc, v)))
  return m
}

function seqNode(doc: Doc, items: unknown[]): YAMLSeq {
  const s = new YAMLSeq(doc.schema)
  for (const x of items) s.add(rawNode(doc, x))
  return s
}

/** Nodes pass through; `Map` is a mapping, an array a sequence, anything else a scalar. */
function rawNode(doc: Doc, v: unknown): Node {
  if (v instanceof YAMLMap || v instanceof YAMLSeq || v instanceof Scalar) return v
  if (v instanceof Map) return mapNode(doc, [...v.entries()])
  if (Array.isArray(v)) return seqNode(doc, v)
  return doc.createNode(v) as Scalar
}

function entriesNode(doc: Doc, entries: Entries<unknown>): YAMLMap {
  return mapNode(doc, entries)
}

function blockNode(doc: Doc, block: MappingBlock): YAMLMap {
  const out: Array<[YamlKey, unknown]> = []
  if (block.annotate) out.push(['annotate', entriesNode(doc, block.annotate)])
  if (block.translate) out.push(['translate', entriesNode(doc, block.translate)])
  for (const e of block.extra) if (!(BLOCK_KEYS as readonly string[]).includes(String(e[0]))) out.push(e)
  return mapNode(doc, out)
}

function ruleNode(doc: Doc, rule: EventRuleModel): YAMLMap {
  const out: Array<[YamlKey, unknown]> = []
  if (rule.firstMatch !== undefined) out.push(['first-match', rule.firstMatch])
  if (rule.decoder !== undefined) out.push(['decoder', rule.decoder])
  if (rule.decoderRegex !== undefined) out.push(['decoder-regex', rule.decoderRegex])
  if (rule.keyFormat !== undefined) out.push(['event-key-format', rule.keyFormat])
  if (rule.decoderHeader !== undefined) out.push(['decoder-header', rule.decoderHeader])
  out.push(['event-key-mapping', mapNode(doc, rule.mappings.map((m) => [m.key, blockNode(doc, m)] as [YamlKey, unknown]))])
  if (rule.fallback) out.push(['fallback', blockNode(doc, rule.fallback)])
  if (rule.globals) out.push(['globals', blockNode(doc, rule.globals)])
  if (rule.subs) out.push(['subs', mapNode(doc, rule.subs.map(([field, e]) => [field, entriesNode(doc, e)] as [YamlKey, unknown]))])
  if (rule.translate) out.push(['translate', entriesNode(doc, rule.translate)])
  for (const e of rule.extra) if (!(RULE_KEYS as readonly string[]).includes(String(e[0]))) out.push(e)
  return mapNode(doc, out)
}

function schemaVersionNode(doc: Doc, text: string): Node {
  const m = /^-?\d+\.(\d+)$/.exec(text)
  if (m) {
    const s = doc.createNode(Number(text)) as Scalar
    s.minFractionDigits = m[1]!.length
    return s
  }
  if (/^-?\d+$/.test(text)) return doc.createNode(Number(text)) as Scalar
  return doc.createNode(text) as Scalar
}

/** Render the model as extractor YAML in the backend's own style. */
export function toYaml(model: ExtractorModel): string {
  const doc = new YAML.Document(undefined, YAML_OPTS)
  const top: Array<[YamlKey, unknown]> = []
  if (model.schemaVersion !== undefined) top.push(['schema-version', schemaVersionNode(doc, model.schemaVersion)])
  if (model.extractorVersion !== undefined) top.push(['extractor-version', model.extractorVersion])
  if (model.extractorId !== undefined) top.push(['extractor-id', model.extractorId])
  if (model.integration !== undefined) top.push(['integration', model.integration])
  if (model.vendorName !== undefined) top.push(['vendor-name', model.vendorName])
  if (model.productName !== undefined) top.push(['product-name', model.productName])
  if (model.sourceName !== undefined) top.push(['source-name', model.sourceName])
  if (model.sourceType !== undefined) top.push(['source-type', model.sourceType])
  if (model.sourceDescription !== undefined) top.push(['source-description', model.sourceDescription])
  if (model.kvRegex !== undefined) top.push(['kv-regex', model.kvRegex])
  top.push(['provides-streams', seqNode(doc, model.providesStreams)])
  top.push(['master-filters', seqNode(doc, model.masterFilters)])
  if (model.globals) top.push(['globals', blockNode(doc, model.globals)])
  top.push(['event-details', seqNode(doc, model.rules.map((r) => ruleNode(doc, r)))])
  for (const e of model.extra) if (!(TOP_KEYS as readonly string[]).includes(String(e[0]))) top.push(e)
  doc.contents = mapNode(doc, top)
  return explicitEmptyKeys(doc.toString(OUT))
}

/**
 * The backend's dumper writes a mapping keyed by the empty string as
 *   ? ''
 *   : annotate: …
 * (95 of 216 corpus extractors, the "every matched line" idiom). The yaml
 * library writes `'':`; rewrite it so the output matches what the backend sends.
 */
function explicitEmptyKeys(text: string): string {
  const lines = text.split('\n')
  const out: string[] = []
  for (let i = 0; i < lines.length; i++) {
    const m = /^(\s*)'':$/.exec(lines[i]!)
    const next = lines[i + 1]
    if (m && next !== undefined && next.startsWith(m[1] + '  ') && next.trim()) {
      out.push(`${m[1]}? ''`, `${m[1]}: ${next.slice(m[1]!.length + 2)}`)
      i++
    } else out.push(lines[i]!)
  }
  return out.join('\n')
}

// ---------------------------------------------------------------- helpers

/** True when the YAML is exactly what the model would write — the form can own it without losing a byte. */
export function isCanonical(yaml: string): boolean {
  try { return toYaml(fromYaml(yaml)) === yaml } catch { return false }
}

/** True when any level carries keys outside the grammar the form offers. */
export function hasExtras(model: ExtractorModel): boolean {
  if (model.extra.length || model.globals?.extra.length) return true
  if ([...model.providesStreams, ...model.masterFilters].some((x) => x instanceof Map)) return true
  return model.rules.some((r) => r.extra.length || r.fallback?.extra.length || r.globals?.extra.length || r.mappings.some((m) => m.extra.length))
}

/** The streams the model writes: mapping streams plus non-OTHER fallback streams (corpus rule, W5.0). */
export function writtenStreams(model: ExtractorModel): string[] {
  const seen = new Set<string>()
  const add = (block: MappingBlock | undefined, includeOther: boolean) => {
    const s = block?.annotate?.find(([k]) => k === 'Stream')?.[1]
    if (s === undefined || s === null || s === '') return
    if (!includeOther && String(s) === 'OTHER') return
    seen.add(String(s))
  }
  for (const r of model.rules) {
    for (const m of r.mappings) add(m, true)
    add(r.fallback, false)
  }
  return [...seen]
}

/** The model behind `newExtractorTemplate` — the create page's starter, one regex rule on one stream. */
export function newExtractorModel(name: string, stream = 'AUTHENTICATION'): ExtractorModel {
  const source = (name || 'MY-SOURCE').toUpperCase().replace(/[^A-Z0-9-]+/g, '-')
  return {
    schemaVersion: '1.0',
    integration: 'Syslog',
    vendorName: 'Vendor',
    productName: 'Product',
    sourceName: source,
    sourceType: 'APPLICATION',
    sourceDescription: `Extractor for ${name || 'a new source'}`,
    providesStreams: [stream],
    masterFilters: ['.'],
    rules: [{
      firstMatch: '(?P<msg>.+)',
      decoder: 'regex',
      decoderRegex: '^(?P<host>\\S+)\\s+(?P<msg>.+)$',
      keyFormat: 'EVENT',
      mappings: [{ key: 'EVENT', annotate: [['Stream', stream], ['Action', 'EVENT']], translate: [['host', 'System']], extra: [] }],
      fallback: { annotate: [['EventName', `Generic ${name || 'source'} event`], ['Stream', 'OTHER']], extra: [] },
      extra: [],
    }],
    extra: [],
  }
}
